import { useParams, Link } from 'react-router-dom'
import { useGetModel } from '../../../../api/routes/models'
import { useGetModelAttributes } from '../../../../api/routes/model-attributes'
import { useGetConfigTemplates } from '../../../../api/routes/config-templates'
import { useGetAllErrors } from '../../../../api/routes/errors'
import { useGetDashboards } from '../../../../api/routes/dashboards'
import { useGetRules } from '../../../../api/routes/rules'
import {
    ArrowLeft,
    ListTree,
    AlertTriangle,
    Settings2,
    LayoutDashboard,
    BellRing,
    ChevronRight,
} from 'lucide-react'

const HubCard = ({
    to,
    icon,
    title,
    description,
    count,
}: {
    to: string
    icon: React.ReactNode
    title: string
    description: string
    count?: number
}) => (
    <Link
        to={to}
        className="group rounded-lg border bg-card p-5 hover:border-primary/30 hover:shadow-sm transition-all"
    >
        <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
                <div className="rounded-md bg-muted p-2.5 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                    {icon}
                </div>
                <div>
                    <h3 className="font-medium text-sm text-foreground">
                        {title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {description}
                    </p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                {count != null && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                        {count}
                    </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-colors" />
            </div>
        </div>
    </Link>
)

export const SingleModelRoute = () => {
    const { id: modelId } = useParams<{ id: string }>()
    const { data: model, isLoading: modelLoading } = useGetModel(modelId!)
    const { data: attributes } = useGetModelAttributes(modelId!)
    const { data: templates } = useGetConfigTemplates(modelId!)
    const { data: errors } = useGetAllErrors(modelId)
    const { data: dashboards } = useGetDashboards(modelId!)
    const { data: rules } = useGetRules(modelId!)

    if (modelLoading) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                Loading...
            </div>
        )
    }

    if (!model) {
        return (
            <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
                Model not found.
            </div>
        )
    }

    return (
        <div className="mx-auto py-6 lg:py-10 px-4 sm:px-6 lg:px-8 xl:px-32">
            <div className="mb-6">
                <Link
                    to="/models"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Models
                </Link>
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                        {model.name}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Manage model configuration and definitions.
                    </p>
                </div>
            </div>

            <div className="grid gap-3">
                <HubCard
                    to={`/models/${modelId}/attributes`}
                    icon={<ListTree className="h-5 w-5" />}
                    title="Attributes"
                    description="Data attributes and CAN field mappings"
                    count={attributes?.length}
                />
                <HubCard
                    to={`/models/${modelId}/errors`}
                    icon={<AlertTriangle className="h-5 w-5" />}
                    title="Error Definitions"
                    description="Error codes and descriptions for this model"
                    count={errors?.length}
                />
                <HubCard
                    to={`/models/${modelId}/config-templates`}
                    icon={<Settings2 className="h-5 w-5" />}
                    title="Config Templates"
                    description="Configuration templates for device settings"
                    count={templates?.length}
                />
                <HubCard
                    to={`/models/${modelId}/dashboards`}
                    icon={<LayoutDashboard className="h-5 w-5" />}
                    title="Dashboards"
                    description="Live dashboard layouts shown for this model's devices"
                    count={dashboards?.length}
                />
                <HubCard
                    to={`/models/${modelId}/rules`}
                    icon={<BellRing className="h-5 w-5" />}
                    title="Alerts"
                    description="Get notified when a device needs attention"
                    count={rules?.length}
                />
            </div>
        </div>
    )
}
